import type { TFunction } from "i18next";
import { Archive } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { Button } from "@/components/ui/v2/button";
import { useArchiveSegment } from "@/features/segments/api/useArchiveSegment";
import { ActionTooltip } from "@/features/segments/components/ActionTooltip";
import type { SegmentDetailResponse } from "@/features/segments/types";
import useToast from "@/lib/hooks/useToast";

const IN_USE_ERROR = "Segment.Archive.InUse";

type ArchiveSegmentActionProps = {
  segment: Pick<SegmentDetailResponse, "id" | "name" | "status">;
  clientId?: string;
  variant: "button" | "icon";
  onArchived: () => void;
};

const getArchiveTooltip = (
  t: TFunction,
  variant: ArchiveSegmentActionProps["variant"],
  canArchive: boolean,
) => {
  if (canArchive && variant === "icon") return t("admin.segments.archive.button");
  if (canArchive) return t("admin.segments.archive.help");
  if (variant === "icon") return t("admin.segments.archive.inUse");
  return `${t("admin.segments.archive.help")} ${t("admin.segments.archive.inUse")}`;
};

export function ArchiveSegmentAction({
  segment,
  clientId,
  variant,
  onArchived,
}: ArchiveSegmentActionProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const archive = useArchiveSegment();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const canArchive = segment.status === "Inactive";
  const tooltip = getArchiveTooltip(t, variant, canArchive);

  const handleConfirm = () =>
    archive.mutate(
      { segmentId: segment.id, clientId },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          showToast(
            t("admin.segments.archive.success", { name: segment.name }),
            "success",
          );
          onArchived();
        },
        onError: (error) => {
          setConfirmOpen(false);
          const inUse = error.response?.data?.title === IN_USE_ERROR;
          showToast(
            inUse
              ? t("admin.segments.archive.inUseError")
              : t("admin.segments.archive.error"),
            "error",
          );
        },
      },
    );

  return (
    <>
      <ActionTooltip tooltip={tooltip} disabled={!canArchive}>
        {variant === "icon" ? (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={!canArchive}
            aria-label={t("admin.segments.archive.button")}
            onClick={() => setConfirmOpen(true)}
            data-testid="archive-segment"
          >
            <Archive />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="text-destructive"
            disabled={!canArchive}
            onClick={() => setConfirmOpen(true)}
            data-testid="archive-segment"
          >
            <Archive />
            {t("admin.segments.archive.button")}
          </Button>
        )}
      </ActionTooltip>
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => !archive.isPending && setConfirmOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.segments.archive.dialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-wrap">
              {t("admin.segments.archive.dialog.description", {
                name: segment.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archive.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault();
                handleConfirm();
              }}
              loading={archive.isPending}
              disabled={archive.isPending}
            >
              <Archive />
              {t("admin.segments.archive.dialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
